/**
 * KillFeed — WITH KILLER ATTRIBUTION.
 *
 * v1's feed was spectator-only and every line it could produce was "X died"
 * (there was no killer to name — nothing tracked who dealt the damage, because
 * death was a client claim). Here the server decides the killer and ships it on
 * the `eliminated` event as `killerId`, so the feed can say who did it, with
 * what, and to whom.
 *
 * Three cases, and all three matter:
 *   - a player kill      → "VOLT ⚔ MIRAGE"
 *   - the zone           → "TALON ⌁ zone"    (killerId === null, byZone)
 *   - your own kill/death→ highlighted, because it is the only line that changes
 *                          what you should do next
 *
 * Names are WHITE, never the roster colour. A roster colour here would put
 * NVDA green and TSLA red in a list of who lost — exactly the two hues this
 * palette reserves for value direction. The treatment is styles.css's; this
 * file only says which span is which.
 */

const MAX_ROWS = 5;
const ROW_LIFETIME_MS = 6500;
const FADE_MS = 260;

export class KillFeed {
  constructor(localUserId) {
    this.root = document.getElementById('kill-feed');
    this.localUserId = localUserId;
    this._rows = [];
  }

  setLocalUserId(id) {
    this.localUserId = id;
  }

  /**
   * @param {object} ev the server's `eliminated` event
   * @param {(id:string)=>({name:string, characterId:string}|null)} lookup
   */
  onElimination(ev, lookup) {
    const victim = lookup(ev.victimId);
    const killer = ev.killerId ? lookup(ev.killerId) : null;

    const row = document.createElement('div');
    row.className = 'kf-row';
    if (ev.victimId === this.localUserId || ev.killerId === this.localUserId) {
      row.classList.add('mine');
    }

    const killerSpan = document.createElement('span');
    killerSpan.className = 'killer';
    if (killer) {
      killerSpan.textContent = killer.name;
    } else {
      // No killer id means the zone or the void took them. Naming it beats a
      // bare "died", which tells a player nothing about what to avoid. It is
      // not a player, so it is not shouted — dim, not red: the zone is a rule
      // of the map, not money leaving.
      killerSpan.textContent = 'THE ZONE';
      killerSpan.classList.add('zone');
    }

    const verb = document.createElement('span');
    verb.className = 'verb';
    verb.textContent = killer ? '⚔' : '⌁';

    const victimSpan = document.createElement('span');
    victimSpan.className = 'victim';
    victimSpan.textContent = victim ? victim.name : ev.victimId.slice(0, 8);

    const place = document.createElement('span');
    place.className = 'place';
    place.textContent = `#${ev.placement}`;

    row.append(killerSpan, verb, victimSpan, place);
    this.root.appendChild(row);

    const entry = { el: row, born: performance.now() };
    this._rows.push(entry);
    while (this._rows.length > MAX_ROWS) this._retire(this._rows.shift());
  }

  update() {
    const now = performance.now();
    for (let i = this._rows.length - 1; i >= 0; i--) {
      const entry = this._rows[i];
      const age = now - entry.born;
      if (age > ROW_LIFETIME_MS + FADE_MS) {
        entry.el.remove();
        this._rows.splice(i, 1);
      } else if (age > ROW_LIFETIME_MS) {
        entry.el.classList.add('fading');
      }
    }
  }

  _retire(entry) {
    if (!entry) return;
    entry.el.remove();
  }

  clear() {
    for (const entry of this._rows) entry.el.remove();
    this._rows.length = 0;
  }
}
